/**
 * In-house wishlist. Replaces the dead Swym integration but keeps its
 * `.swym-button.swym-add-to-wishlist` markup/classes so existing CSS keeps working.
 * Guest state lives in localStorage; logged-in state merges with a server-side
 * wishlist via Zigly's own Lambda (see plan doc for the API contract).
 */
(function () {
  if (window.__ziglyWishlistInit) return;
  window.__ziglyWishlistInit = true;

  var STORAGE_KEY = 'zigly_wishlist_handles';
  var WISHLIST_API_BASE = 'https://ayd6gqbcj8.execute-api.ap-south-1.amazonaws.com';
  var BUTTON_SELECTOR = '.swym-button.swym-add-to-wishlist[data-product-handle]';

  function getWishlist() {
    var raw = localStorage.getItem(STORAGE_KEY) || '';
    return raw
      .split(',')
      .map(function (handle) {
        return handle.trim();
      })
      .filter(Boolean);
  }

  function setWishlist(handles) {
    var unique = handles.filter(function (handle, index) {
      return handle && handles.indexOf(handle) === index;
    });
    localStorage.setItem(STORAGE_KEY, unique.join(','));
    return unique;
  }

  function toggleHandle(handle) {
    var handles = getWishlist();
    var index = handles.indexOf(handle);
    var added;
    if (index === -1) {
      handles.push(handle);
      added = true;
    } else {
      handles.splice(index, 1);
      added = false;
    }
    return { added: added, handles: setWishlist(handles) };
  }

  function updateButtonState(button, wishlisted) {
    button.classList.toggle('is-wishlisted', wishlisted);
    button.setAttribute('aria-pressed', wishlisted ? 'true' : 'false');
  }

  function syncAllButtons(root) {
    var scope = root || document;
    var handles = getWishlist();
    scope.querySelectorAll(BUTTON_SELECTOR).forEach(function (button) {
      updateButtonState(button, handles.indexOf(button.getAttribute('data-product-handle')) !== -1);
    });
  }

  function updateHeaderCounters(handles) {
    var count = handles.length;
    document.querySelectorAll('.swym-wishlist-header-counter').forEach(function (el) {
      el.textContent = count > 0 ? count : '';
      el.classList.toggle('hidden-wishlist-count', count === 0);
    });
  }

  function publishWishlistUpdate(detail) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.wishlistUpdate) {
      publish(PUB_SUB_EVENTS.wishlistUpdate, detail);
    }
  }

  function applyToggle(button) {
    var handle = button.getAttribute('data-product-handle');
    if (!handle) return;

    var result = toggleHandle(handle);
    syncAllButtons(document);
    updateHeaderCounters(result.handles);
    publishWishlistUpdate({ handle: handle, added: result.added, handles: result.handles });

    if (window.ZIGLY_CUSTOMER_ID) {
      syncToggleToServer(handle, result.added);
    }
  }

  function handleClick(event) {
    var button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;
    event.preventDefault();
    applyToggle(button);
  }

  function handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    var button = event.target.closest(BUTTON_SELECTOR);
    if (!button) return;
    event.preventDefault();
    applyToggle(button);
  }

  function syncToggleToServer(handle, added) {
    fetch(WISHLIST_API_BASE + '/customer/wishlist/toggle', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_id: 'gid://shopify/Customer/' + window.ZIGLY_CUSTOMER_ID,
        handle: handle,
        action: added ? 'add' : 'remove',
      }),
    }).catch(function () {
      // Non-blocking: localStorage already reflects the toggle regardless of network result.
    });
  }

  function mergeWishlistAfterLogin() {
    if (!window.ZIGLY_CUSTOMER_ID) return;
    var mergeFlagKey = 'zigly_wishlist_merged_' + window.ZIGLY_CUSTOMER_ID;
    if (localStorage.getItem(mergeFlagKey)) return;

    fetch(WISHLIST_API_BASE + '/customer/wishlist/merge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_id: 'gid://shopify/Customer/' + window.ZIGLY_CUSTOMER_ID,
        handles: getWishlist(),
      }),
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        if (!data || !Array.isArray(data.handles)) return;
        var handles = setWishlist(data.handles);
        localStorage.setItem(mergeFlagKey, '1');
        syncAllButtons(document);
        updateHeaderCounters(handles);
        publishWishlistUpdate({ handle: null, added: null, handles: handles });
      })
      .catch(function () {
        // Merge failed silently; local wishlist stays usable and the next page load retries.
      });
  }

  function observeForNewButtons() {
    if (!document.body || typeof MutationObserver === 'undefined') return;

    var observer = new MutationObserver(function (mutations) {
      var handles = getWishlist();
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;

          if (node.matches && node.matches(BUTTON_SELECTOR)) {
            updateButtonState(node, handles.indexOf(node.getAttribute('data-product-handle')) !== -1);
          }

          if (node.querySelectorAll) {
            node.querySelectorAll(BUTTON_SELECTOR).forEach(function (button) {
              updateButtonState(button, handles.indexOf(button.getAttribute('data-product-handle')) !== -1);
            });
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  document.addEventListener('click', handleClick);
  document.addEventListener('keydown', handleKeydown);

  document.addEventListener('DOMContentLoaded', function () {
    syncAllButtons(document);
    updateHeaderCounters(getWishlist());
    mergeWishlistAfterLogin();
    observeForNewButtons();
  });

  // Exposed so the wishlist page can re-sync heart state after injecting fetched cards.
  window.ziglyWishlist = {
    getWishlist: getWishlist,
    syncAllButtons: syncAllButtons,
  };
})();
